import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

// ── Default fallback portrait images (interleaved L/R by index) ──────────────
const DEFAULT_PORTRAITS = [
  "https://images.unsplash.com/photo-1531956656798-56686eeef3d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1529636798458-92182e662485?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1524638431109-93d95c968f03?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

// ── Default fallback cake overlay images ──────────────────────────────────────
const DEFAULT_CAKES = [
  "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1604702389032-28cff7432c48?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1629576690723-bb3a22ae7126?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1659401083689-240a4f511a76?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1517427294546-5aa121f68e8a?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1655762755958-cc0e10095c24?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1602663491496-73f07481dbea?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1700448293876-07dca826c161?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
];

// ── Confetti decorations ──────────────────────────────────────────────────────
const CONFETTI = [
  { color: "#E39BA6", round: true,  w: 12, h: 12, top: "14%", left: "3%"    },
  { color: "#93C5FD", round: false, w: 7,  h: 22, top: "72%", left: "5%",   rotate: 15  },
  { color: "#FCD34D", round: true,  w: 9,  h: 9,  top: "83%", left: "10%"   },
  { color: "#86EFAC", round: false, w: 24, h: 7,  top: "26%", left: "31%",  rotate: -45 },
  { color: "#F9A8D4", round: true,  w: 15, h: 15, top: "89%", left: "34%"   },
  { color: "#93C5FD", round: false, w: 6,  h: 20, top: "15%", left: "33%",  rotate: 20  },
  { color: "#E39BA6", round: false, w: 24, h: 6,  top: "18%", right: "31%", rotate: 12  },
  { color: "#F9A8D4", round: true,  w: 14, h: 14, top: "80%", right: "34%"  },
  { color: "#FCD34D", round: false, w: 6,  h: 20, top: "10%", right: "5%",  rotate: -12 },
  { color: "#86EFAC", round: true,  w: 9,  h: 9,  top: "20%", right: "3%"   },
  { color: "#CD838E", round: false, w: 22, h: 6,  top: "87%", right: "6%",  rotate: 10  },
  { color: "#93C5FD", round: true,  w: 11, h: 11, top: "55%", left: "1.5%"  },
  { color: "#F9A8D4", round: false, w: 20, h: 5,  top: "45%", right: "1.5%", rotate: 45 },
  { color: "#FCD34D", round: true,  w: 7,  h: 7,  top: "35%", left: "7%"    },
  { color: "#86EFAC", round: true,  w: 6,  h: 6,  top: "60%", right: "7%"   },
];

/**
 * Crossfade image panel — stacks all images and transitions opacity to active.
 *
 * NOTE: the outer div handles positioning (uses the passed className/style so the
 * caller controls absolute placement). The inner div is `relative` so it acts as
 * the stacking context for the absolutely-positioned images. Keeping them separate
 * avoids the Tailwind CSS cascade conflict where `.relative` overrides `.absolute`
 * when both appear on the same element (`.relative` is generated later in the sheet).
 */
function FadePanel({ images, activeIdx, className, style }) {
  if (!images || images.length === 0) return null;
  const active = ((activeIdx % images.length) + images.length) % images.length;
  return (
    <div className={className || ""} style={style}>
      <div className="relative w-full h-full overflow-hidden">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center"
            style={{
              opacity: i === active ? 1 : 0,
              transition: "opacity 1500ms ease-in-out",
            }}
            loading={i === 0 ? "eager" : "lazy"}
          />
        ))}
      </div>
    </div>
  );
}

export default function Hero() {
  const [portraits, setPortraits]   = useState(DEFAULT_PORTRAITS);
  const [cakeImgs, setCakeImgs]     = useState(DEFAULT_CAKES);
  const [tick, setTick]             = useState(0);

  useEffect(() => {
    // Fetch portrait images from site settings (admin-managed)
    api.get("/settings")
      .then((r) => {
        if (Array.isArray(r.data?.hero_images) && r.data.hero_images.length >= 2) {
          setPortraits(r.data.hero_images);
        }
      })
      .catch(() => {});

    // Fetch cake images from cakes collection for overlays
    api.get("/cakes")
      .then((r) => {
        if (Array.isArray(r.data)) {
          const urls = r.data.map((c) => c.image_url).filter(Boolean);
          if (urls.length >= 4) setCakeImgs(urls);
        }
      })
      .catch(() => {});

    // Single 2.5s ticker: portraits change every 2 ticks (5s), cakes every 3 ticks (7.5s)
    const id = setInterval(() => setTick((t) => t + 1), 2500);
    return () => clearInterval(id);
  }, []);

  // Split portrait pool into left / right panels (odd / even index)
  const leftPortraits  = portraits.filter((_, i) => i % 2 === 0);
  const rightPortraits = portraits.filter((_, i) => i % 2 === 1);

  // Staggered indices: right panel lags by one tick (2.5s offset)
  const leftIdx  = Math.floor(tick / 2);
  const rightIdx = Math.floor((tick + 1) / 2);
  const cakeIdx  = Math.floor(tick / 3);
  const n        = cakeImgs.length;

  return (
    <section
      className="relative w-full mt-16 lg:mt-[8.5rem] bg-white overflow-hidden min-h-[560px] h-[calc(100svh-4rem)] lg:h-[calc(100svh-8.5rem)]"
      data-testid="hero-section"
    >
      {/* ── Confetti ─────────────────────────────────────────────────────────── */}
      {CONFETTI.map((c, i) => (
        <div
          key={i}
          aria-hidden="true"
          className="absolute pointer-events-none"
          style={{
            zIndex: 40,
            top: c.top, left: c.left, right: c.right,
            width: c.w, height: c.h,
            backgroundColor: c.color,
            borderRadius: c.round ? "50%" : 2,
            transform: c.rotate ? `rotate(${c.rotate}deg)` : undefined,
          }}
        />
      ))}

      {/* ══════════════ MOBILE (< md) ══════════════════════════════════════════ */}
      <div className="md:hidden absolute inset-0 flex">
        <FadePanel images={leftPortraits}  activeIdx={leftIdx}  className="w-1/2 h-full" />
        <FadePanel images={rightPortraits} activeIdx={rightIdx} className="w-1/2 h-full" />
      </div>
      {/* Overlay for text legibility */}
      <div className="md:hidden absolute inset-0 bg-black/45" style={{ zIndex: 5 }} />
      <div
        className="md:hidden absolute inset-0 flex flex-col items-center justify-center text-center px-6"
        style={{ zIndex: 10 }}
      >
        <h1
          className="font-display font-black text-white leading-[0.9]"
          style={{ fontSize: "clamp(3rem, 12vw, 5rem)" }}
          data-testid="hero-title"
        >
          Cake<br />Central
        </h1>
        <Link
          to="/gallery"
          className="mt-6 inline-block bg-[#EC008C] text-white text-[11px] font-semibold tracking-[0.35em] uppercase px-8 py-3 hover:bg-[#C4007A] transition-colors"
        >
          EXPLORE
        </Link>
      </div>

      {/* ══════════════ DESKTOP (>= md) ════════════════════════════════════════ */}

      {/* Left portrait — crossfading */}
      <FadePanel
        images={leftPortraits}
        activeIdx={leftIdx}
        className="hidden md:block absolute inset-y-0 left-0"
        style={{ width: "37%" }}
      />

      {/* Right portrait — crossfading, 2.5s offset */}
      <FadePanel
        images={rightPortraits}
        activeIdx={rightIdx}
        className="hidden md:block absolute inset-y-0 right-0"
        style={{ width: "37%" }}
      />

      {/* ── Centre white column: cake · title · EXPLORE · cake (no overlap) ──── */}
      <div
        className="hidden md:flex absolute inset-y-0 flex-col items-center justify-center gap-6 lg:gap-8 bg-white px-4"
        style={{ left: "37%", right: "37%", zIndex: 30 }}
      >
        {/* Top cake — crossfading, contained within the centre column */}
        <div className="shadow-lg overflow-hidden w-32 lg:w-44 aspect-square shrink-0">
          <FadePanel images={cakeImgs} activeIdx={cakeIdx % n} className="w-full h-full" />
        </div>

        <div className="text-center">
          <h1
            className="font-display font-black text-ink leading-[0.9]"
            style={{ fontSize: "clamp(2.4rem, 4.5vw, 4.75rem)" }}
            data-testid="hero-title"
          >
            Cake<br />Central
          </h1>
          <Link
            to="/gallery"
            className="mt-6 inline-block bg-[#EC008C] text-white text-[11px] font-semibold tracking-[0.35em] uppercase px-8 py-3 hover:bg-[#C4007A] transition-colors"
          >
            EXPLORE
          </Link>
        </div>

        {/* Bottom cake — crossfading, offset index */}
        <div className="shadow-lg overflow-hidden w-32 lg:w-44 aspect-square shrink-0">
          <FadePanel images={cakeImgs} activeIdx={(cakeIdx + 1) % n} className="w-full h-full" />
        </div>
      </div>
    </section>
  );
}
